/**
 * The video sales page's four behaviours. The page is six sections, so each one renders only the
 * hooks it needs and every initialiser below is guarded on its own hook being present:
 *
 *   [data-vl-phrases]  the power-phrase typewriter (hero)
 *   [data-vl-situations] the situation picker (situations)
 *   [data-vl-media]    the unlocked state (guide, pricing and final call also read it)
 *   [data-vl-preview]  the free preview and its cutoff (guide)
 *
 * The panel is the page's one piece of theatre: it types out the phrases the paid guide teaches,
 * one after another, so a visitor reads three of them before reaching the price.
 *
 * The phrases come from the product metafield, serialised into `data-vl-phrases`, so the copy is
 * merchant-owned and this file never needs editing to change a word.
 *
 * The hero already prints phrase 1 server-side. This only ever replaces text that is already
 * there, so with JavaScript off, with motion reduced, or before the module loads, the panel still
 * reads as a finished quote.
 *
 * The preview cutoff runs on the real player rather than a painted bar. The supplied design drew
 * its own 3px progress bar; over a real `<video controls>` that is a second bar disagreeing with
 * the first, so the player's own progress is the only one shown.
 */

const VL_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const TYPE_MS = 45;
const ERASE_MS = 22;
const HOLD_MS = 1900;
const GAP_MS = 350;

/**
 * The local receipt of a purchase: `?purchased=1` and the `mlx_purchased` cookie both land here,
 * so a visitor who has just paid does not see the wall again on the next page load. The server
 * never sees this — it is the fallback for the second between checkout returning and Flow writing
 * the customer tag.
 */
const VL_OWNED_KEY = 'mlx_video_owned';

/**
 * @param {Element} panel
 */
function initPhraseTypewriter(panel) {
  const out = panel.querySelector('[data-vl-type]');
  const cursor = /** @type {HTMLElement | null} */ (panel.querySelector('.vl-phrase__cursor'));
  if (!out) return;

  /** @type {unknown[]} */
  let raw;
  try {
    raw = JSON.parse(panel.getAttribute('data-vl-phrases') || '[]');
  } catch (error) {
    console.debug('Video phrases are not valid JSON', error);
    return;
  }
  if (!Array.isArray(raw)) return;

  const phrases = raw.map((phrase) => String(phrase).trim()).filter(Boolean);

  // One phrase is not a rotation, it is a caption. Leave the server-rendered text alone.
  const first = phrases[0];
  if (!first || phrases.length < 2 || VL_MOTION.matches) return;

  if (cursor) cursor.hidden = false;

  let phraseIndex = 0;
  let charIndex = first.length;
  let typing = false;

  const tick = () => {
    if (!panel.isConnected) return;

    const phrase = phrases[phraseIndex];
    if (!phrase) return;

    if (typing) {
      charIndex += 1;
      out.textContent = phrase.slice(0, charIndex);
      if (charIndex >= phrase.length) {
        typing = false;
        window.setTimeout(tick, HOLD_MS);
        return;
      }
      window.setTimeout(tick, TYPE_MS);
      return;
    }

    charIndex -= 1;
    out.textContent = phrase.slice(0, charIndex);
    if (charIndex <= 0) {
      typing = true;
      phraseIndex = (phraseIndex + 1) % phrases.length;
      window.setTimeout(tick, GAP_MS);
      return;
    }
    window.setTimeout(tick, ERASE_MS);
  };

  // Nothing runs until the panel is actually on screen, so a visitor never arrives to a phrase
  // already half-erased.
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.disconnect();
        window.setTimeout(tick, HOLD_MS);
      }
    },
    { threshold: 0.5 }
  );
  observer.observe(panel);
}

/**
 * @returns {string[]} every key this browser has a receipt for, lower-cased and de-duplicated.
 */
function readOwnedKeys() {
  /** @type {string[]} */
  let list = [];
  try {
    const local = JSON.parse(localStorage.getItem(VL_OWNED_KEY) || '[]');
    if (Array.isArray(local)) list = list.concat(local);
  } catch (error) {
    // A corrupt or unreadable store is the same as no receipt, but say so out loud: the value is
    // written by other scripts on this storefront, so a broken one is worth seeing in the console.
    console.debug('Video receipt unreadable in localStorage', error);
  }
  try {
    const sess = JSON.parse(sessionStorage.getItem(VL_OWNED_KEY) || '[]');
    if (Array.isArray(sess)) list = list.concat(sess);
  } catch (error) {
    console.debug('Video receipt unreadable in sessionStorage', error);
  }
  return list
    .map((value) => String(value || '').toLowerCase().trim())
    .filter((value, index, all) => value && all.indexOf(value) === index);
}

/**
 * @param {string[]} keys
 */
function writeOwnedKeys(keys) {
  try {
    localStorage.setItem(VL_OWNED_KEY, JSON.stringify(keys));
  } catch (error) {
    // Private mode, or a full quota. The session copy below is the fallback.
    console.debug('Video receipt not stored in localStorage', error);
  }
  try {
    sessionStorage.setItem(VL_OWNED_KEY, JSON.stringify(keys));
  } catch (error) {
    console.debug('Video receipt not stored in sessionStorage', error);
  }
}

/**
 * @param {HTMLElement} media
 * @returns {string} the product handle as the store spells it, or an empty string.
 */
function mediaHandle(media) {
  return (media.getAttribute('data-vl-handle') || '').toLowerCase().trim();
}

/**
 * @param {HTMLElement} media
 * @returns {string} the product id, or an empty string.
 */
function mediaId(media) {
  return String(media.getAttribute('data-vl-id') || '').trim();
}

/**
 * Turns the two client-side receipts — the URL parameters and the `mlx_purchased` cookie — into
 * stored keys. Runs on every load, owned or not, because it is what makes the page owned in the
 * first place.
 *
 * @param {HTMLElement} media
 */
function ingestReceipts(media) {
  const handle = mediaHandle(media);
  const productId = mediaId(media);

  const params = new URLSearchParams(window.location.search);
  if (
    params.get('purchased') === '1' ||
    params.get('access') === '1' ||
    params.get('unlocked') === '1'
  ) {
    const current = readOwnedKeys();
    if (handle && current.indexOf(handle) === -1) current.push(handle);
    if (productId && current.indexOf(productId) === -1) current.push(productId);
    writeOwnedKeys(current);
  }

  try {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)mlx_purchased=([^;]*)/);
    if (!cookieMatch) return;
    const decoded = decodeURIComponent(cookieMatch[1] || '');
    const current = readOwnedKeys();
    try {
      const parsedCookie = JSON.parse(decoded);
      if (parsedCookie && Array.isArray(parsedCookie.handles)) {
        for (const entry of parsedCookie.handles) {
          const clean = String(entry || '').toLowerCase().trim();
          if (clean && current.indexOf(clean) === -1) current.push(clean);
        }
      }
    } catch {
      // Legacy raw handle, before the cookie became a JSON object.
      const rawHandle = String(decoded || '').toLowerCase().trim();
      if (rawHandle && current.indexOf(rawHandle) === -1) current.push(rawHandle);
    }
    writeOwnedKeys(current);
  } catch (error) {
    console.debug('Video receipt cookie unavailable', error);
  }
}

/**
 * The unlocked state. The server resolves ownership from the customer tag; this resolves the two
 * client-side receipts on top of it, and when either says "owned" it removes the gate the server
 * rendered: the lock, the hidden attribute on the player, the preview CTA and its wall, and every
 * jump to a pricing band that is not on the page any more.
 *
 * The selector list is an explicit opt-in attribute, not "any link containing -pricing": the
 * pricing band is a cross-section anchor now, so that pattern would also match the page's own nav.
 */
function initVideoUnlock() {
  const media = /** @type {HTMLElement | null} */ (document.querySelector('[data-vl-media]'));
  if (!media) return;

  const handle = mediaHandle(media);
  const productId = mediaId(media);
  if (!handle && !productId) return;

  ingestReceipts(media);

  const owned = readOwnedKeys();
  const isOwned =
    media.getAttribute('data-vl-owns') === 'true' ||
    (handle !== '' && owned.indexOf(handle) !== -1) ||
    (productId !== '' && owned.indexOf(productId) !== -1);
  if (!isOwned) return;

  // 1. Unhide video player
  const video = media.querySelector('[data-vl-video]');
  if (video) {
    video.removeAttribute('hidden');
    /** @type {HTMLElement} */ (video).style.display = 'block';
  }

  // 2. Remove the free-preview furniture that now sits over a real player
  media.querySelector('[data-vl-play]')?.remove();
  media.querySelector('[data-vl-wall]')?.remove();

  // 3. Hide every jump to the buy card, the player bar, and the two bands that only sell
  document.querySelectorAll('[data-vl-buy], .vl-fp__bar, .vl-pricing, .vl-final').forEach((el) => {
    /** @type {HTMLElement} */ (el).style.display = 'none';
  });
}

/**
 * The free preview: the real player, cut off `data-vl-preview` seconds in. The CTA is what starts
 * it, because the player is rendered hidden for anybody who does not own the video.
 */
function initPreviewWall() {
  const media = /** @type {HTMLElement | null} */ (
    document.querySelector('[data-vl-media][data-vl-preview]')
  );
  if (!media || media.hasAttribute('data-vl-preview-armed')) return;

  const play = /** @type {HTMLElement | null} */ (media.querySelector('[data-vl-play]'));
  const wall = /** @type {HTMLElement | null} */ (media.querySelector('[data-vl-wall]'));
  // An owner's markup prints neither, and `initVideoUnlock` deletes both the moment a receipt
  // says the visitor is one.
  if (!play || !wall) return;
  media.setAttribute('data-vl-preview-armed', '');

  const holder = media.querySelector('[data-vl-video]');
  const video = /** @type {HTMLVideoElement | null} */ (media.querySelector('video'));
  const cutoff = Number(media.getAttribute('data-vl-preview') || '');

  play.addEventListener('click', () => {
    play.hidden = true;
    media.classList.add('is-playing');

    // An iframe cannot be stopped at a trustworthy timestamp. Never expose the full paid guide as
    // a "preview"; show the same wall immediately until a Shopify-hosted video is uploaded.
    if (!video || !(cutoff > 0)) {
      wall.classList.add('is-open');
      return;
    }

    if (holder) holder.removeAttribute('hidden');
    video.play().catch((error) => {
      // A blocked play() leaves the player visible with its own controls, which is the fallback.
      console.debug('Preview play was blocked', error);
    });
  });

  // The non-video fallback is handled by the click listener above.
  if (!video || !(cutoff > 0)) return;

  const openWall = () => {
    video.pause();
    video.hidden = true;
    wall.classList.add('is-open');
  };

  const onTimeUpdate = () => {
    if (video.currentTime < cutoff) return;
    video.removeEventListener('timeupdate', onTimeUpdate);
    openWall();
  };

  video.addEventListener('timeupdate', onTimeUpdate);
}

function initVideoLanding() {
  for (const panel of document.querySelectorAll('[data-vl-phrases]:not([data-vl-ready])')) {
    panel.setAttribute('data-vl-ready', '');
    initPhraseTypewriter(panel);
  }

  for (const picker of document.querySelectorAll('[data-vl-situations]:not([data-vl-ready])')) {
    picker.setAttribute('data-vl-ready', '');
    for (const trigger of picker.querySelectorAll('[data-vl-situation-trigger]')) {
      trigger.addEventListener('click', (event) => {
        const href = trigger.getAttribute('href') || '';
        if (!href.startsWith('#')) return;

        const panel = picker.querySelector(href);
        if (!(panel instanceof HTMLElement)) return;

        event.preventDefault();
        picker.querySelectorAll('[data-vl-situation-trigger]').forEach((item) => {
          item.classList.toggle('is-active', item === trigger);
        });
        picker.querySelectorAll('[data-vl-situation-panel]').forEach((item) => {
          item.classList.toggle('is-selected', item === panel);
        });
      });
    }
  }

  // Order matters: the unlock has to delete the preview CTA before the wall looks for it.
  if (document.querySelector('[data-vl-media]')) initVideoUnlock();
  initPreviewWall();
}

initVideoLanding();

// Re-run when the theme editor swaps a section in.
document.addEventListener('shopify:section:load', initVideoLanding);

// A receipt written in another tab unlocks the video in this one without a reload.
window.addEventListener('storage', (event) => {
  if (event.key !== VL_OWNED_KEY) return;
  initVideoLanding();
});
